import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProfile, Profile as ProfileType } from '../services/profile';
import { deleteProject } from '../services/projects';
import AppShell from '../components/AppShell';
import { useIsMobile } from '../hooks/useIsMobile';
import OutOfCreditsPopup from '../components/OutOfCreditsPopup';
import { IconPlus, IconArrow, IconMore, IconTrash } from '../components/Icons';

const TILE_GRADIENTS = ['#7A4A3B', '#3B6B7A', '#56487A', '#3F5544', '#7A6A3B'];

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [error, setError] = useState('');
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreditsPopup, setShowCreditsPopup] = useState<'credits' | 'projects' | null>(null);

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch(() => setError(t('profile.loadError', 'Unable to load profile')));
  }, [t]);

  const handleDeleteProject = (projectId: string) => {
    setProjectToDelete(projectId);
    setShowDeleteConfirm(true);
    setOpenMenuFor(null);
  };

  const confirmDelete = async () => {
    if (projectToDelete && profile) {
      setIsDeleting(true);
      try {
        await deleteProject(projectToDelete);
        setProfile({ ...profile, projects: profile.projects.filter((p) => p.id !== projectToDelete) });
        setShowDeleteConfirm(false);
        setProjectToDelete(null);
      } catch {
        setError(t('profile.deleteError', 'Failed to delete project. Please try again.'));
        setShowDeleteConfirm(false);
        setProjectToDelete(null);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setProjectToDelete(null);
  };

  const createNewProject = () => {
    if (!profile?.credits.canCreateProject) {
      setShowCreditsPopup(profile?.credits.hasCredits ? 'projects' : 'credits');
      return;
    }
    navigate('/project-setup');
  };

  if (error && !profile) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div
          style={{
            padding: '14px 20px',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-wash)',
            color: 'var(--danger)',
            borderRadius: 12,
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="9" opacity="0.25" />
            <path d="M21 12a9 9 0 0 0-9-9" />
          </svg>
          {t('profile.loading', 'Loading your projects…')}
        </div>
      </div>
    );
  }

  return (
    <AppShell
      active="projects"
      breadcrumbs={[{ label: t('sidebar.workspace', 'Workspace') }, { label: t('sidebar.projects', 'Projects') }]}
      credits={profile.credits.remainingCredits}
      creditsMax={profile.credits.totalCredits}
      projectsUsed={profile.credits.currentProjectCount}
      projectsMax={profile.credits.maxProjects}
    >
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '24px 16px' : '32px 36px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
          <h1 className="display tight" style={{ fontSize: isMobile ? 26 : 34, margin: 0 }}>
            {t('profile.title', 'Projects')}
          </h1>
          <button type="button" className="btn primary" onClick={createNewProject}>
            <IconPlus />
            {t('profile.newProject', 'New project')}
            <span className="kbd on-accent" style={{ marginLeft: 2 }}>N</span>
          </button>
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 22 }}>
          {profile.credits.currentProjectCount} {t('profile.of', 'of')} {profile.credits.maxProjects} {t('profile.used', 'used')} ·{' '}
          <span className="mono">
            {profile.credits.remainingCredits} {t('profile.creditsRemaining', 'credits remaining')}
          </span>
        </div>

        {/* Sort row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 14, borderBottom: '1px solid var(--line)' }}>
          <div style={{ flex: 1 }} />
          <span className="eyebrow">{t('profile.sort', 'Sort')}</span>
          <button type="button" className="nav-link">
            {t('profile.recent', 'Recent ↓')}
          </button>
        </div>

        {profile.projects.length === 0 ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: '64px 0', gap: 12, textAlign: 'center' }}>
            <div className="display" style={{ fontSize: 22 }}>{t('profile.empty.title', 'No projects yet')}</div>
            <div className="muted-2" style={{ fontSize: 13 }}>{t('profile.empty.body', 'Start your first project — Entire takes it from a prompt to a build.')}</div>
            <button type="button" onClick={createNewProject} className="btn primary" style={{ marginTop: 8 }}>
              <IconPlus />
              {t('profile.newProject', 'New project')}
            </button>
          </div>
        ) : (
          <div>
            {profile.projects.map((project, i) => (
              <div
                key={project.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '56px 1fr' : '56px minmax(0, 1.6fr) minmax(0, 1fr) 140px',
                  gap: isMobile ? 12 : 18,
                  alignItems: 'center',
                  padding: '20px 0',
                  borderBottom: i < profile.projects.length - 1 ? '1px solid var(--line)' : 'none',
                }}
              >
                <Link to={`/projects/${project.id}`} style={{ textDecoration: 'none' }}>
                  {project.thumbnailUrl ? (
                    <img
                      src={project.thumbnailUrl}
                      alt={project.name}
                      style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--line)' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        background: `linear-gradient(135deg, ${TILE_GRADIENTS[i % TILE_GRADIENTS.length]}, var(--surface) 110%)`,
                        border: '1px solid var(--line)',
                      }}
                    />
                  )}
                </Link>
                <Link to={`/projects/${project.id}`} style={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{project.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-5)', marginTop: 3 }}>v{project.version}</div>
                </Link>
                {!isMobile && (
                  <div className="muted-2" style={{ fontSize: 13 }}>
                    {t('profile.updatedRecently', 'Updated recently')}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, position: 'relative', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                  <button
                    type="button"
                    className="btn ghost sm icon"
                    onClick={() => setOpenMenuFor(openMenuFor === project.id ? null : project.id)}
                  >
                    <IconMore />
                  </button>
                  {openMenuFor === project.id && (
                    <div
                      className="card"
                      style={{
                        position: 'absolute',
                        top: 36,
                        right: 0,
                        zIndex: 20,
                        padding: 4,
                        minWidth: 160,
                        background: 'var(--surface)',
                        boxShadow: '0 18px 36px -18px rgba(0,0,0,0.5)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleDeleteProject(project.id)}
                        className="side-link"
                        style={{ width: '100%', color: 'var(--accent)', background: 'transparent', border: 'none' }}
                      >
                        <span className="ic" style={{ color: 'var(--accent)' }}>
                          <IconTrash />
                        </span>
                        {t('profile.delete', 'Delete')}
                      </button>
                    </div>
                  )}
                  <Link to={`/projects/${project.id}`} className="btn sm">
                    {t('profile.open', 'Open')}
                    <IconArrow />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreditsPopup && (
        <OutOfCreditsPopup type={showCreditsPopup} onClose={() => setShowCreditsPopup(null)} />
      )}

      {showDeleteConfirm && (
        <div className="modal-backdrop" onClick={cancelDelete}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 8 }}>
              {t('profile.deleteEyebrow', 'Delete project')}
            </div>
            <h3 className="display tight" style={{ fontSize: 24, margin: '0 0 10px' }}>
              {t('profile.deleteTitle', 'This cannot be undone.')}
            </h3>
            <p className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.55, margin: '0 0 22px' }}>
              {t('profile.deleteBody', 'All project data — chats, code, builds — will be permanently lost.')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn" onClick={cancelDelete} disabled={isDeleting}>
                {t('common:cancel', 'Cancel')}
              </button>
              <button type="button" className="btn primary" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? t('profile.deleting', 'Deleting…') : t('profile.confirmDelete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
